import { useCallback, useEffect, useRef, useState } from 'react';
import {
  BTN_SIZE,
  MARGIN,
  STORAGE_KEY,
  clampLauncher,
  cornerPos,
  isMobileViewport,
  parseStoredPosition,
  positionFromStored,
  settle,
  windowPosition,
  type Corner,
  type Point,
  type StoredPosition,
  type Viewport,
} from './launcher-geometry';

/**
 * Dragging the launcher, and keeping the window with it.
 *
 * The Angular screen did this with the CDK's free-drag, whose position is an
 * offset from wherever CSS put the element, so every calculation had to be
 * converted in and out of viewport coordinates first. Pointer events place the
 * button directly, and the arithmetic is `launcher-geometry`.
 *
 * A drag has to be told from a click, since the same button does both: any
 * movement past a few pixels makes it a drag, and the click that follows the
 * release is then ignored.
 */

/** Enough movement to mean a drag rather than an unsteady click. */
const DRAG_SLOP = 3;

function viewportSize(): Viewport {
  return { width: window.innerWidth, height: window.innerHeight };
}

/**
 * The lowest the launcher may go, so it cannot be parked behind the app's
 * header. In this app's own dev server there is no host header and the margin
 * stands on its own.
 */
function measureTopBound(): number {
  const header = document.querySelector('iqd-menu-bar');
  const height = header instanceof HTMLElement ? header.offsetHeight : 0;
  return Math.max(MARGIN, height + MARGIN);
}

function persist(value: StoredPosition) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Storage can be unavailable; the launcher just starts from the corner
    // next time.
  }
}

export interface Launcher {
  /** Top-left of the button. Ignored while `isMobile`, which pins it by CSS. */
  position: Point;
  /** Top-left of the chat window, placed against the button. */
  windowPosition: Point;
  isDragging: boolean;
  isMobile: boolean;
  /** True when the release that just happened ended a drag, not a click. */
  wasDragged: () => boolean;
  onPointerDown: (event: React.PointerEvent<HTMLElement>) => void;
}

export function useLauncher(): Launcher {
  const [viewport, setViewport] = useState<Viewport>(() =>
    typeof window === 'undefined' ? { width: 1024, height: 768 } : viewportSize(),
  );
  const [topBound, setTopBound] = useState(MARGIN);
  const [position, setPosition] = useState<Point>(() => cornerPos('right', viewport));
  const [isDragging, setIsDragging] = useState(false);

  const cornerRef = useRef<Corner | null>('right');
  const dragMovedRef = useRef(false);
  // Where in the button the pointer went down, so it does not jump to centre
  // itself under the cursor.
  const grabRef = useRef<Point>({ x: 0, y: 0 });

  const isMobile = isMobileViewport(viewport);

  // Restore where it was left, once the header is measurable.
  useEffect(() => {
    const size = viewportSize();
    const bound = measureTopBound();
    const stored = parseStoredPosition(localStorage.getItem(STORAGE_KEY));
    const restored = positionFromStored(stored, size, bound);
    cornerRef.current = restored.corner;
    setViewport(size);
    setTopBound(bound);
    setPosition(restored.position);
  }, []);

  // A docked launcher follows its corner across a resize; a placed one is only
  // pulled back into view.
  useEffect(() => {
    function onResize() {
      const size = viewportSize();
      const bound = measureTopBound();
      setViewport(size);
      setTopBound(bound);
      setPosition((current) =>
        cornerRef.current
          ? cornerPos(cornerRef.current, size)
          : clampLauncher(current, size, bound),
      );
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const onPointerDown = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      dragMovedRef.current = false;
      if (isMobile || event.button !== 0) return;

      const element = event.currentTarget;
      const bounds = element.getBoundingClientRect();
      grabRef.current = { x: event.clientX - bounds.left, y: event.clientY - bounds.top };
      const origin = { x: event.clientX, y: event.clientY };
      element.setPointerCapture(event.pointerId);

      const move = (moveEvent: PointerEvent) => {
        if (
          !dragMovedRef.current &&
          Math.hypot(moveEvent.clientX - origin.x, moveEvent.clientY - origin.y) < DRAG_SLOP
        ) {
          return;
        }
        dragMovedRef.current = true;
        setIsDragging(true);
        setPosition(
          clampLauncher(
            { x: moveEvent.clientX - grabRef.current.x, y: moveEvent.clientY - grabRef.current.y },
            viewportSize(),
            topBound,
          ),
        );
      };

      const up = () => {
        element.releasePointerCapture(event.pointerId);
        element.removeEventListener('pointermove', move);
        element.removeEventListener('pointerup', up);
        element.removeEventListener('pointercancel', up);
        if (!dragMovedRef.current) return;
        setIsDragging(false);
        setPosition((current) => {
          const settled = settle(current, viewportSize(), topBound);
          cornerRef.current = settled.corner;
          persist(settled.corner ? { corner: settled.corner } : settled.position);
          return settled.position;
        });
      };

      element.addEventListener('pointermove', move);
      element.addEventListener('pointerup', up);
      element.addEventListener('pointercancel', up);
    },
    [isMobile, topBound],
  );

  const wasDragged = useCallback(() => dragMovedRef.current, []);

  return {
    position,
    windowPosition: windowPosition(position, viewport, topBound),
    isDragging,
    isMobile,
    wasDragged,
    onPointerDown,
  };
}

export { BTN_SIZE };
